/**
 * Loan products — the terms the business lends on.
 *
 * These were constants in the codebase; changing a rate meant a deploy. The
 * screen makes two things explicit that a bare form would not: a change only
 * affects new applications (live loans keep the schedule they were written
 * on), and the effect of the new numbers is previewed before saving, so
 * nobody discovers what "15% over 24 months" means to a borrower only after
 * it is live.
 */
import { useCallback, useEffect, useState } from "react";
import { Info, Loader2, RotateCcw, Save } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";

type Product = Record<string, any>;

/** Same amortisation the server prices with — a preview, never the quote. */
function monthlyPayment(principal: number, annualRate: number, months: number) {
  if (!principal || !months) return 0;
  if (!annualRate) return principal / months;
  const r = annualRate / 100 / 12;
  return (principal * r * (1 + r) ** months) / ((1 + r) ** months - 1);
}

const FIELDS = [
  { key: "min_amount", label: "Minimum amount", suffix: "ZMW", step: "1000" },
  { key: "max_amount", label: "Maximum amount", suffix: "ZMW", step: "1000" },
  { key: "max_term_months", label: "Maximum term", suffix: "months", step: "1" },
  { key: "interest_rate", label: "Interest rate", suffix: "% per year", step: "0.1" },
] as const;

function ProductCard({
  product, onSaved,
}: { product: Product; onSaved: () => void }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<Product>(product);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(product), [product]);

  const dirty = FIELDS.some(
    (f) => Number(draft[f.key]) !== Number(product[f.key]));

  const save = async () => {
    setSaving(true);
    try {
      await staffApi.updateProduct(product.tier, {
        name: draft.name,
        min_amount: Number(draft.min_amount),
        max_amount: Number(draft.max_amount),
        max_term_months: Number(draft.max_term_months),
        interest_rate: Number(draft.interest_rate),
      });
      toast({
        title: `${product.name} updated`,
        description: "New applications will be priced on these terms.",
      });
      onSaved();
    } catch (error) {
      // The server owns validation; show exactly what it objected to.
      toast({
        title: "Could not save",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Preview at the midpoint of the band over the full term — a representative
  // loan rather than an edge case.
  const sample = Math.round((Number(draft.min_amount) + Number(draft.max_amount)) / 2);
  const term = Number(draft.max_term_months) || 1;
  const monthly = monthlyPayment(sample, Number(draft.interest_rate), term);
  const totalCost = monthly * term - sample;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span>{product.name}</span>
          <div className="flex items-center gap-2">
            {product.id === null && (
              <Badge className="bg-muted text-muted-foreground">using defaults</Badge>
            )}
            {dirty && <Badge className="bg-warning/10 text-warning">unsaved</Badge>}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-xs" htmlFor={`${product.tier}-${f.key}`}>
                {f.label}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`${product.tier}-${f.key}`}
                  type="number"
                  step={f.step}
                  value={draft[f.key] ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, [f.key]: e.target.value })}
                />
                <span className="w-20 shrink-0 text-xs text-muted-foreground">
                  {f.suffix}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <p className="text-xs text-muted-foreground">
            A {formatCurrency(sample)} loan over {term} month{term === 1 ? "" : "s"}
            {" "}at {draft.interest_rate}% would repay
          </p>
          <p className="mt-1 font-medium tabular-nums">
            {formatCurrency(monthly)} per month
            <span className="text-muted-foreground">
              {" · "}{formatCurrency(totalCost)} total interest
            </span>
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {product.updated_at
              ? `Last changed ${new Date(product.updated_at).toLocaleString()}`
              : "Never changed"}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" disabled={!dirty || saving}
              onClick={() => setDraft(product)}>
              <RotateCcw className="mr-1 h-4 w-4" />Reset
            </Button>
            <Button size="sm" disabled={!dirty || saving} onClick={save}>
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                : <Save className="mr-1 h-4 w-4" />}
              Save changes
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ProductsPage() {
  const { toast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProducts((await staffApi.products()).products);
    } catch (error) {
      toast({
        title: "Could not load products",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Loan Products</h1>
        <p className="text-sm text-muted-foreground">
          The amounts, terms and rates each plan tier is lent on.
        </p>
      </header>

      <Card>
        <CardContent className="flex items-start gap-3 py-4 text-sm">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            Changes apply to <strong>new applications only</strong>. Loans already
            running keep the rate and schedule they were written on. Every change is
            recorded in the audit trail with the old and new values.
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin" />
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {products.map((p) => (
            <ProductCard key={p.tier} product={p} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}
